import React from 'react';

import { StatusBadge } from '../StatusBadge';

import { CertificatesMenu } from 'components/UI/CertificatesMenu';

import { useSelector } from 'store/hooks';

import { Box, Typography } from '@mui/material';

import { font } from 'settings/tokens';

import type { CertificateCustomer } from 'types';

interface ProductPageHeaderProps {
  certificate: CertificateCustomer;
  showStatus?: boolean;
}

import { useAuth } from 'hooks';
import { isCertificateOwnedBy } from 'utils/certificateUtils';

export const ProductPageHeader = ({ certificate, showStatus = true }: ProductPageHeaderProps) => {
  const { isAuthenticated } = useAuth();

  const { profile } = useSelector(({ auth }) => auth);

  const isCertificateOwner = isCertificateOwnedBy(certificate, profile?.id);
  const isShowMenu = false;
  const isShowStatus =
    showStatus &&
    isAuthenticated &&
    (isCertificateOwner || certificate?.status === 'waiting_accept' || certificate?.status === 'cancel_request');

  return (
    <Box sx={{ position: 'relative' }}>
      {/* No right padding: it reserved room for CertificatesMenu, which is disabled
          (`isShowMenu = false`), and the status chip now occupies that side of the row
          instead — so the title gets the space back (VERA-476). */}
      {/* VERA-315 Phase 2: identity is the object's NAME, and nothing else.
          `font.title` (24), up from 20 — the page's largest type after the
          photograph, which is what makes it read as the subject rather than as
          the first of several equal-weight lines. */}
      {/*
        ⚠️ `sx`, not the `fontSize` PROP, and the difference is not stylistic.

        The `h1` VARIANT (settings/mui/typography.ts) is 32px with a responsive
        bump to 40 at `sm`:

            h1: { fontSize: font.display, '@media(min-width:600px)': { fontSize: 40 } }

        A `fontSize` prop emits a rule with no media query, so it won over the
        variant's BASE and lost to the variant's MEDIA rule. The comment above
        has said 24 since VERA-315; measured in the browser, this title rendered
        24px on a phone and **40px at every width from 600 up** — so the size the
        component asked for was the one it got only on mobile, and nobody looking
        at the source could have seen it.

        Setting it through `sx` with its own `up('sm')` entry states the size at
        BOTH widths, so there is no width at which the variant's bump is the
        thing that decides it.

        THE TWO SIZES ARE BOTH TOKENS, and deliberately so. `font.title` (24) on
        a phone, `font.display` (32) from `sm` up — the record column is ~476px
        beside a large photograph there, so 24 reads smaller against it than the
        same 24 does in a 343px column on a phone, and the page subject stops
        holding the page. 32 is not a split-the-difference number: it is the
        app's display step and the `h1` variant's own BASE, so the desktop title
        lands on the scale rather than beside it.

        40 (the variant's `sm` bump) was too much — measured, it wrapped this
        product's name onto two lines in the desktop column and read as a banner
        rather than a title.

        To change either size, change it HERE, where it is visible — not via the
        variant's bump, which moves every other `h1` in the app.
      */}
      <Typography
        variant='h1'
        sx={theme => ({
          fontWeight: 400,
          lineHeight: 1.2,
          fontSize: `${font.title}px`,
          [theme.breakpoints.up('sm')]: { fontSize: `${font.display}px` },
        })}
      >
        {certificate?.product?.title}
      </Typography>

      {isShowStatus && (
        <Box mt={1.5}>
          {/*
            `dot`, matching the certificates list.

            The page used to take the filled `chip` on the argument that here the
            status is a headline fact about one object rather than a marker on one of
            nine rows. That reads well in isolation and badly in sequence: you tap a
            card showing a quiet dot and land on a page showing a saturated pill for
            the SAME fact, so the status appears to change on navigation when nothing
            did. One grammar across both surfaces wins; colour still carries severity.
          */}
          <StatusBadge
            status={certificate?.status}
            isCertificateOwner={isCertificateOwner}
            variant='dot'
          />
        </Box>
      )}

      {/* The SKU that sat here is GONE, not moved: the facts table below has
          shown it as `Product ID` the whole time, so the page printed the same
          identifier twice, ~700px apart, with the second one labelled.

          The redesign's Phase 2 called for a muted second line under the title
          (Chrono24's model line, Artsy's italic "title, year"). This product has
          nothing to put there, and it is worth writing down why rather than
          re-litigating it every session:

          - The company is the TENANT whose app you are in, not the item's maker.
            Every seeded product belongs to "Louis Vuitton" while the titles name
            Rolex, Cartier and Hermès, so a brand line would print the same name
            on every certificate in a white-labelled app.
          - `manufactured_at` is set on 1 of 11 seeded products, so a year line
            would be absent almost always.
          - `type` is populated ("watches", "handbags") but it is the one fact the
            PHOTOGRAPH above already carries. Artsy's medium line works because
            "oil on canvas" is not visible in the image; "watches" is.

          So identity here is one line, and the italic waits for a field that
          earns it. */}

      {isShowMenu && <CertificatesMenu certificate={certificate} />}
    </Box>
  );
};
